import { MotionValue } from "framer-motion";
import React from "react";

import {
  IBynderImage,
  RiveAnimationPlayMode,
  TBynderAsset,
} from "@brexhq/shared/types/BynderAsset";

import {
  RiveAnimationPlaybackStateChangedCallback,
  RiveAnimationProgressCallback,
} from "../RiveAnimation/types";
import type {
  VideoProgressCallback,
  VideoPlaybackStateChangedCallback,
} from "../Video/types";

import { BynderImage } from "./Image/BynderImage";
import { buildBynderImageUrl } from "./Image/LegacyImage";
import BynderRiveAnimation from "./RiveAnimation/BynderRiveAnimation";
import BynderVideo from "./Video/BynderVideo";

interface BynderAssetProps {
  asset?: TBynderAsset;
  className?: string;
  border?: boolean;
  //Video Props
  loopVideo?: boolean;
  showVideoControls?: boolean;
  thumbnail?: IBynderImage;
  onVideoProgressUpdate?: VideoProgressCallback;
  onVideoPlaybackStateChanged?: VideoPlaybackStateChangedCallback;
  onVideoEnded?: () => void;
  onVideoPaused?: () => void;
  onVideoPlaying?: () => void;

  //Image Props
  maxWidth?: number;
  altTextOverride?: string;
  /**
   * @deprecated Avoid using this setting for new components
   */
  useLegacyImage?: boolean;
  loading?: "eager" | "lazy";
  programName?: string;

  // Rive Animation Props
  play?: RiveAnimationPlayMode;
  onAnimationProgressUpdate?: RiveAnimationProgressCallback;
  onAnimationPlaybackStateChanged?: RiveAnimationPlaybackStateChangedCallback;
  playbackButton?: boolean;
  animationProgress?: MotionValue<number>;
  onProgressUpdate?: ({ progress }: { progress: number }) => void;
  handleSetActiveSlide?: () => void;
}

/**
 * Component that takes either image or video and renders appropriately.
 * @param asset - Bynder Asset
 * @param loopVideo - loop video [video only]
 * @param showVideoControls - show video controls and don't autoplay [video only]
 * @param thumbnail - optional thumbnail image [video only]
 * @param maxWidth - max width of asset [image only]
 * @param altTextOverride - optional alt text - if not supplied it will generate from Bynder description [image only]
 * @param useLegacyImage - use legacy image redering instead of next/image [image only]
 */
const BynderAsset = React.forwardRef<HTMLVideoElement, BynderAssetProps>(
  (
    {
      asset,
      loopVideo,
      showVideoControls,
      thumbnail,
      play,
      onVideoProgressUpdate,
      onVideoEnded,
      maxWidth,
      altTextOverride,
      className,
      loading,
      useLegacyImage,
      programName,
      onVideoPaused,
      onVideoPlaying,
      playbackButton,
      animationProgress,
      onProgressUpdate,
      handleSetActiveSlide,
    },
    ref,
  ) => {
    if (!asset) {
      return null;
    }

    switch (asset.type) {
      case "VIDEO":
        return (
          <BynderVideo
            {...asset}
            onPause={onVideoPaused}
            onPlaying={onVideoPlaying}
            onVideoEnded={onVideoEnded}
            onProgressUpdate={onVideoProgressUpdate}
            loop={loopVideo}
            ref={ref}
            autoPlay={!showVideoControls}
            muted={!showVideoControls}
            controls={!!showVideoControls}
            className={className}
            programName={programName}
            previewImg={
              buildBynderImageUrl(thumbnail, { width: maxWidth ?? 2000 }) ??
              asset.previewImg
            }
          />
        );
      case "IMAGE":
        return (
          <BynderImage
            image={asset}
            maxWidth={maxWidth}
            altTextOverride={altTextOverride}
            className={className}
            useLegacyImage={useLegacyImage}
            loading={loading}
          />
        );

      case "DOCUMENT": {
        return (
          <BynderRiveAnimation
            type="DOCUMENT"
            // if sanity thumbnail is present, use it, otherwise use bynder thumbnail
            thumbnailUrl={
              thumbnail
                ? thumbnail.previewImg ?? asset.thumbnailUrl
                : asset.thumbnailUrl
            }
            play={play}
            originalUrl={asset.originalUrl}
            aspectRatio={asset.aspectRatio}
            playbackButton={playbackButton}
            previewImg={asset.previewImg}
            animationProgress={animationProgress}
            onProgressUpdate={onProgressUpdate}
            handleSetActiveSlide={handleSetActiveSlide}
          />
        );
      }
    }
  },
);
BynderAsset.displayName = "BynderAsset";

export { BynderAsset };
